<template>
  <tab-layout :full-width="true">
    <calculator-sidebar
      slot="sidebar"
      :draft-name="MOCK_DRAFT_NAME"
      :events="MOCK_EVENTS_LIST"
      :current-event-id="MOCK_EVENT_ID"
    />
    <div slot="tabContent" class="ml-0 row mx-0">
        <div class="col px-0 pr-lg-3">
          <div class="bg-white rounded mb-4 p-3 pb-5">
            <page-heading>Prize Money Calculator</page-heading>
            <h2 class="h3 mb-3">{{ MOCK_EVENT_NAME }}</h2>

            <div class="alert alert-info alert-simple">
              PRIZEMONEY CAN BE CHANGED LATER. PLEASE PRESS "CALCULATE ROUND MONEY" AND "FINALISE EVENT" TO VIEW RESULTS.
            </div>

            <div v-if="MOCK_UNSAVED" class="alert alert-warning">
              <a class="close" href="#" @click.prevent="MOCK_UNSAVED = false">&times;</a>
              There are unsaved changes.
            </div>

            <section class="calc-info mb-5 px-sm-3 py-sm-4">
              <form class="calc-info__numbers">
                <div class="calc-row">
                  <div class="calc-row__heading">Total nominations:</div>
                  <div class="calc-row__property">{{ MOCK_TOTAL_NOMINATIONS }}</div>
                </div>
                <div class="calc-row">
                  <div class="calc-row__heading">Total rounds: <span>(including Final)</span></div>
                  <div class="calc-row__property">{{ MOCK_TOTAL_ROUNDS }}</div>
                </div>
                <div class="calc-row">
                  <div class="calc-row__heading">Declared prize money:</div>
                  <div class="calc-row__property">
                    <div class="calc-row__input-wrap --dollar-icon">
                      <input
                        type="number"
                        class="calc-row__input"
                        v-model="MOCK_DECLARED"
                        @input="MOCK_UNSAVED = true"
                      >
                    </div>
                  </div>
                </div>
                <div class="calc-row">
                  <div class="calc-row__heading">Add back amount: <span>(leave blank if none)</span></div>
                  <div class="calc-row__property">
                    <div class="calc-row__input-wrap --dollar-icon">
                      <input
                        type="number"
                        class="calc-row__input"
                        v-model="MOCK_BACK_AMOUNT"
                        @input="MOCK_UNSAVED = true"
                      >
                    </div>
                  </div>
                </div>
                <div class="calc-row">
                  <div class="calc-row__heading">Total prize money:</div>
                  <div class="calc-row__property">
                    <i class="icon-dollar-sign" />
                    <span>{{ MOCK_TOTAL_PRIZE }}</span>
                  </div>
                </div>
                <div class="calc-info__submit">
                  <input
                    class="btn btn-primary btn-default"
                    type="button"
                    value="Calculate Round Money"
                    @click="onCalculate"
                  >
                </div>
              </form>
            </section>

            <section v-if="MOCK_CALCULATED" class="calc-rounds">
              <h3 class="h5 mb-3">Rounds Prize Money</h3>
              <div class="calc-rounds__wrap">
                <div
                  v-for="(round, idx) in MOCK_ROUNDS"
                  :key="round.id"
                  class="calc-round"
                >
                  <div class="calc-round__header">
                    {{ idx === MOCK_ROUNDS.length - 1 ? 'Final Round' : 'Round ' + (idx + 1) }}
                  </div>
                  <div class="calc-round__body">
                    <div class="calc-round__main-info">
                      <div class="calc-row">
                        <div class="calc-row__heading">Round money:</div>
                        <div class="calc-row__property">
                          <div class="calc-row__input-wrap --dollar-icon">
                            <input type="number" class="calc-row__input" v-model="round.money" @change="onRoundMoneyChange(round)">
                          </div>
                        </div>
                      </div>
                      <div class="calc-row">
                        <div class="calc-row__heading">% of Total:</div>
                        <div class="calc-row__property">
                          <div class="calc-row__input-wrap --percent-icon">
                            <input type="number" class="calc-row__input" v-model="round.percent" @change="onRoundPercentChange(round)">
                          </div>
                        </div>
                      </div>
                      <div class="calc-row">
                        <div class="calc-row__heading">Number of places:</div>
                        <div class="calc-row__property">
                          <div class="calc-row__input-wrap">
                            <app-multiselect
                              :value="placesOptions.find(o => o.value === round.places)"
                              :options="placesOptions"
                              label="label"
                              track-by="value"
                              :allow-empty="false"
                              @input="onPlacesChange(round, $event)"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="calc-round__table round-table round-table_tac">
                      <div class="round-table__header">
                        <div class="round-table__row">
                          <div class="round-table__cell col-4">Place</div>
                          <div class="round-table__cell col-4">
                            <i class="icon-percent" />
                            <i
                              class="round-table__edit"
                              :class="{ active: round.editingColumn === 'percent', disabled: round.editingColumn === 'money' }"
                              @click="toggleColumnEdit(round, 'percent')"
                            />
                          </div>
                          <div class="round-table__cell col-4">
                            <i class="icon-dollar-sign" />
                            <i
                              class="round-table__edit"
                              :class="{ active: round.editingColumn === 'money', disabled: round.editingColumn === 'percent' }"
                              @click="toggleColumnEdit(round, 'money')"
                            />
                          </div>
                        </div>
                      </div>
                      <div class="round-table__body">
                        <div
                          v-for="p in round.placesData"
                          :key="p.place"
                          class="round-table__row"
                        >
                          <div class="round-table__cell col-4">{{ p.place }}</div>
                          <div class="round-table__cell col-4">
                            <input
                              v-if="round.editingColumn === 'percent'"
                              type="number"
                              class="round-table__edit-input"
                              v-model="p.percent"
                            >
                            <template v-else>{{ p.percent }}</template>
                          </div>
                          <div class="round-table__cell col-4">
                            <input
                              v-if="round.editingColumn === 'money'"
                              type="number"
                              class="round-table__edit-input"
                              v-model="p.amount"
                            >
                            <template v-else>{{ p.amount }}</template>
                          </div>
                        </div>
                      </div>
                      <div class="round-table__total">
                        <div class="round-table__row">
                          <div class="round-table__cell col-4">TOTAL</div>
                          <div class="round-table__cell col-4">
                            {{ round.placesData.reduce(function(s, p) { return s + Number(p.percent) }, 0).toFixed(1) }}
                          </div>
                          <div class="round-table__cell col-4">
                            {{ round.placesData.reduce(function(s, p) { return s + Number(p.amount) }, 0).toFixed(2) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-12 d-lg-none mt-4 px-0">
                <app-button :disabled="!MOCK_CALCULATED || submitting" @click="onSave">
                  Finalise Event
                </app-button>
              </div>
            </section>
          </div>
        </div>

        <desktop-action-bar>
          <app-button :disabled="!MOCK_CALCULATED || submitting" @click="onSave">
            Finalise Event
          </app-button>
          <div class="calc-info__small-text mt-2">
            Updates all the actual prize money to the figures below for this event.
            This will overwrite current prize money values but not if paid out.
          </div>
        </desktop-action-bar>
    </div>
  </tab-layout>
</template>

<script>
import { defineComponent, computed, onMounted, watch, getCurrentInstance } from '@vue/composition-api'
import TabLayout from '@/components/TabLayout'
import PageHeading from '@/components/PageHeading'
import AppButton from '@/components/AppButton'
import AppMultiselect from '@/components/AppMultiselect'
import DesktopActionBar from '@/components/DesktopActionBar'
import CalculatorSidebar from '@/components/Calculators/CalculatorSidebar'
import EventBus from '@/store/EventBus'
import useCalculatorView from '@/composables/calculators/useCalculatorView'

export default defineComponent({
  name: 'CalculatorView',
  components: { TabLayout, PageHeading, AppButton, AppMultiselect, DesktopActionBar, CalculatorSidebar },
  setup() {
    const vm = getCurrentInstance()
    const router = vm.proxy.$router
    const eventId = computed(() => vm.proxy.$route.params.eventId)

    const {
      draftName,
      eventsList,
      eventName,
      totalNominations,
      totalRounds,
      declaredPrize,
      backAmount,
      totalPrize,
      rounds,
      loading,
      submitting,
      hasCalculated,
      unsaved,
      setEventId,
      fetchCalculator,
      calculateRounds,
      onRoundMoneyChange,
      onRoundPercentChange,
      onRoundPlacesChange,
      toggleColumnEdit,
      saveCalculator
    } = useCalculatorView(eventId.value)

    onMounted(() => {
      if (eventId.value) fetchCalculator()
    })

    watch(eventId, (newId) => {
      if (newId) {
        setEventId(newId)
        fetchCalculator()
      }
    })

    // Places 3-50 (CAM-1235) — searchable via app-multiselect so a secretary can type
    // a place number instead of scrolling a 48-option list.
    const placesOptions = Array.from({ length: 48 }, (_, i) => i + 3).map(value => ({ value, label: String(value) }))

    function onPlacesChange(round, option) {
      if (option == null) return
      onRoundPlacesChange(round, option.value)
    }

    function onCalculate() {
      calculateRounds()
    }

    // Monolith redirects to /draws/view after a successful save — mirror that.
    async function onSave() {
      const result = await saveCalculator()
      if (!result.success) {
        EventBus.$emit('http-error', result.message)
        return
      }
      router.push('/draws/dashboard').then(() => {
        if (result.message) EventBus.$emit('show-success', result.message)
      })
    }

    return {
      MOCK_DRAFT_NAME: draftName,
      MOCK_EVENTS_LIST: eventsList,
      MOCK_EVENT_ID: computed(() => Number(eventId.value)),
      MOCK_EVENT_NAME: eventName,
      MOCK_TOTAL_NOMINATIONS: totalNominations,
      MOCK_TOTAL_ROUNDS: totalRounds,
      MOCK_DECLARED: declaredPrize,
      MOCK_BACK_AMOUNT: backAmount,
      MOCK_UNSAVED: unsaved,
      MOCK_CALCULATED: hasCalculated,
      MOCK_TOTAL_PRIZE: totalPrize,
      MOCK_ROUNDS: rounds,
      loading,
      submitting,
      placesOptions,
      onPlacesChange,
      onRoundMoneyChange,
      onRoundPercentChange,
      toggleColumnEdit,
      onCalculate,
      onSave
    }
  }
})
</script>



// WEBPACK FOOTER //
// src/views/Calculators/CalculatorView.vue